import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/api/client";
import { Card, XPBar, ShadowBadge, Spinner } from "@/components/ui";
import Button from "@/components/ui/Button";
import { useAuthStore } from "@/store/authStore";
import { XPRing, getLevel, getLevelProgress } from "@/components/ui/XPBar";
import { getShadow } from "@/components/ui/ShadowBadge";
import { getLevelSpan, getHonorRank, getNextHonorRank, levelStartXp, MAX_LEVEL } from "@/lib/levels";
import toast from "react-hot-toast";
import clsx from "clsx";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = import.meta.env.VITE_API_URL?.replace("/api", "") || "http://localhost:4000";

const AVATARS = [
  "🦊","🐺","🦁","🐯","🐻","🐼","🦅","🦋","🐲","🦄",
  "🤖","👾","🧙","🦸","🥷","🕵️","👑","🌟","⚡","🔥",
  "🌙","🎮","🎯","🚀","💎","🛡️","⚔️","🎪","🧊","🌊",
];

const RARITY_META = {
  common:    { color: "#9ca3af", label: "Common"    },
  rare:      { color: "#3b82f6", label: "Rare"      },
  epic:      { color: "#8b5cf6", label: "Epic"      },
  legendary: { color: "#fbbf24", label: "Legendary" },
};

/* ── Stat Tile ─────────────────────────────────────────────────────────────── */
function StatTile({ icon, label, value, color, sub, onClick }) {
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      className={clsx(
        "flex flex-col items-center gap-1 p-3 rounded-2xl border border-nexus-border relative overflow-hidden transition-all duration-200",
        onClick ? "hover:scale-[1.04] hover:border-current/30 active:scale-[0.97] cursor-pointer" : "cursor-default",
      )}
      style={{ background: `${color}08` }}
    >
      <div className="absolute inset-x-0 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${color}50, transparent)` }} />
      <span className="text-xl" style={{ filter: `drop-shadow(0 0 8px ${color}70)` }}>{icon}</span>
      <span className="font-black text-sm tabular-nums leading-none" style={{ color }}>{value}</span>
      <span className="text-[9px] font-bold uppercase tracking-wider text-gray-600">{label}</span>
      {sub && <span className="text-[9px] text-gray-700">{sub}</span>}
    </button>
  );
}

/* ── Deine Reise — Level-System 2.0 journey panel ─────────────────────────────
   Membership age, honor-rank road, next-rank progress and the XP source
   breakdown from /xp/summary. The whole point: progress here is measured in
   MONTHS and YEARS — this panel makes the long road feel epic, not punishing. */
const SOURCE_META = {
  quiz:        { label: "Quizzes",        icon: "🎯", color: "#8b5cf6" },
  task:        { label: "Aufgaben",       icon: "✅", color: "#22c55e" },
  daily:       { label: "Daily Quests",   icon: "📅", color: "#06b6d4" },
  achievement: { label: "Achievements",   icon: "🏆", color: "#fbbf24" },
  multiplayer: { label: "Multiplayer",    icon: "⚔️", color: "#ec4899" },
  minigame:    { label: "Minigames",      icon: "🎮", color: "#f97316" },
  season:      { label: "Events",         icon: "🎉", color: "#a78bfa" },
  teacher:     { label: "Lehrer-Bonus",   icon: "🎁", color: "#67e8f9" },
  shop:        { label: "Coin-Burn",      icon: "🔥", color: "#ef4444" },
  admin:       { label: "Bonus",          icon: "✨", color: "#c084fc" },
  misc:        { label: "Sonstiges",      icon: "🌀", color: "#94a3b8" },
};

function membershipText(createdAt) {
  if (!createdAt) return null;
  const start = new Date(createdAt);
  const months = Math.max(0, Math.floor((Date.now() - start.getTime()) / (30.44 * 24 * 3600 * 1000)));
  const dur = months < 1 ? "frisch dabei"
    : months < 12 ? `${months} ${months === 1 ? "Monat" : "Monate"} dabei`
    : `${Math.floor(months / 12)} ${Math.floor(months / 12) === 1 ? "Jahr" : "Jahre"}${months % 12 ? ` ${months % 12} Mon.` : ""} dabei`;
  return { since: start.toLocaleDateString("de-AT", { month: "long", year: "numeric" }), dur };
}

function JourneyPanel({ xp, user }) {
  const { data } = useQuery({
    queryKey: ["xp", "summary"],
    queryFn: () => api.get("/xp/summary").then((r) => r.data),
    staleTime: 60_000,
  });
  const summary = data?.summary;

  const level    = getLevel(xp);
  const span     = getLevelSpan(xp);
  const honor    = getHonorRank(level);
  const next     = getNextHonorRank(level);
  const member   = membershipText(user?.createdAt);
  const nextPct  = next
    ? Math.min(100, Math.max(0, ((xp - levelStartXp(honor.level)) / (levelStartXp(next.level) - levelStartXp(honor.level))) * 100))
    : 100;
  const bySource = Object.entries(summary?.bySource || {}).sort((a, b) => b[1] - a[1]).slice(0, 5);
  const maxSrc   = bySource[0]?.[1] || 1;
  const today    = summary?.today;

  return (
    <Card className="p-5 relative overflow-hidden">
      <div className="absolute inset-x-0 top-0 h-0.5" style={{ background: `linear-gradient(90deg, transparent, ${honor.color}, transparent)` }} />
      <div className="flex items-center justify-between mb-4">
        <p className="font-black text-sm flex items-center gap-2"><span>🛣️</span><span>Deine Reise</span></p>
        {member && (
          <span className="text-[10px] font-bold text-gray-500">
            Mitglied seit {member.since} · <span className="text-violet-400">{member.dur}</span>
          </span>
        )}
      </div>

      {/* honor rank road */}
      <div className="flex items-center gap-3 mb-1.5">
        <div className="w-12 h-12 rounded-2xl grid place-items-center text-2xl border"
          style={{ background: `${honor.color}14`, borderColor: `${honor.color}45`, boxShadow: `0 0 18px ${honor.color}30` }}>
          {honor.emoji}
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-black text-sm" style={{ color: honor.color }}>{honor.name} <span className="text-gray-500 font-bold">· Level {level}{level >= MAX_LEVEL ? " · MAX" : ""}</span></p>
          <p className="text-[11px] text-gray-500 leading-snug">{honor.description}</p>
        </div>
      </div>

      {next && (
        <div className="mt-3">
          <div className="flex justify-between text-[10px] font-bold mb-1">
            <span className="text-gray-500">Weg zu {next.emoji} <span style={{ color: next.color }}>{next.name}</span> (Lv {next.level})</span>
            <span className="text-gray-600">{(levelStartXp(next.level) - xp).toLocaleString("de-AT")} XP übrig</span>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }}>
            <div className="h-full rounded-full transition-all duration-1000"
              style={{ width: `${nextPct}%`, background: `linear-gradient(90deg, ${honor.color}, ${next.color})`, boxShadow: `0 0 10px ${next.color}60` }} />
          </div>
          <p className="text-[10px] text-gray-600 mt-1.5">
            Dieses Level: {span.current.toLocaleString("de-AT")} / {span.needed.toLocaleString("de-AT")} XP · Die Reise ist auf JAHRE gebaut — jeder Tag zählt. 🧗
          </p>
        </div>
      )}

      {/* today's energy (daily soft-cap, framed positively) */}
      {today && (
        <div className="mt-4 p-3 rounded-xl border border-nexus-border" style={{ background: "rgba(139,92,246,0.05)" }}>
          <div className="flex justify-between text-[10px] font-bold mb-1">
            <span className="text-violet-300">⚡ Heutige Lern-Energie</span>
            <span className="text-gray-500">{today.earned.toLocaleString("de-AT")} / {today.tiers.max} XP</span>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }}>
            <div className="h-full rounded-full" style={{
              width: `${Math.min(100, (today.earned / today.tiers.max) * 100)}%`,
              background: "linear-gradient(90deg,#8b5cf6,#06b6d4)",
            }} />
          </div>
          <p className="text-[9px] text-gray-600 mt-1">Qualität schlägt Grind: Nach {today.tiers.full} XP am Tag zählt jedes weitere XP weniger — morgen ist die Energie wieder voll.</p>
        </div>
      )}

      {/* XP sources — last 30 days */}
      {bySource.length > 0 && (
        <div className="mt-4">
          <p className="text-[10px] font-black uppercase tracking-widest text-gray-600 mb-2">Woher deine XP kommen (30 Tage)</p>
          <div className="space-y-1.5">
            {bySource.map(([src, amt]) => {
              const m = SOURCE_META[src] || SOURCE_META.misc;
              return (
                <div key={src} className="flex items-center gap-2">
                  <span className="text-sm w-6 text-center">{m.icon}</span>
                  <span className="text-[11px] font-bold text-gray-400 w-24 shrink-0">{m.label}</span>
                  <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.05)" }}>
                    <div className="h-full rounded-full" style={{ width: `${(amt / maxSrc) * 100}%`, background: m.color, boxShadow: `0 0 8px ${m.color}50` }} />
                  </div>
                  <span className="text-[10px] font-black tabular-nums w-14 text-right" style={{ color: m.color }}>+{amt.toLocaleString("de-AT")}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Card>
  );
}

/* ── Rank Medal ────────────────────────────────────────────────────────────── */
function RankMedal({ rank }) {
  if (!rank) return null;
  const medal = rank === 1 ? { emoji: "🥇", color: "#fbbf24" }
               : rank === 2 ? { emoji: "🥈", color: "#9ca3af" }
               : rank === 3 ? { emoji: "🥉", color: "#f97316" }
               : { emoji: null, color: "#8b5cf6" };
  return (
    <div
      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-black border"
      style={{ color: medal.color, borderColor: `${medal.color}40`, background: `${medal.color}10` }}
    >
      {medal.emoji ? <span>{medal.emoji}</span> : <span style={{ color: medal.color }}>#{rank}</span>}
      {!medal.emoji && <span>Platz</span>}
    </div>
  );
}

/* ── Activity Bar ──────────────────────────────────────────────────────────── */
function ActivityBar({ value, max, color, label }) {
  const pct = Math.min((value / max) * 100, 100);
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-[10px] font-bold">
        <span className="text-gray-500">{label}</span>
        <span style={{ color }}>{value.toLocaleString("de-DE")}</span>
      </div>
      <div className="h-1.5 rounded-full overflow-hidden" style={{ background: `${color}15` }}>
        <div
          className="h-full rounded-full transition-all duration-1000"
          style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${color}80, ${color})`, boxShadow: `0 0 6px ${color}60` }}
        />
      </div>
    </div>
  );
}

/* ── Main Component ─────────────────────────────────────────────────────────── */
export default function Profile() {
  const { user, updateAvatar, refreshMe, logout } = useAuthStore();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [showAvatarPicker, setShowAvatarPicker] = useState(false);
  const [avatarTab, setAvatarTab] = useState("emoji");
  const [uploading, setUploading] = useState(false);
  const [showCoreHint, setShowCoreHint] = useState(false);
  const fileInputRef = useRef(null);

  // ── Universal profile (bio/interests/links) — same /api/profile/* backend
  //    that powers Create, Clubs, and Cinema. ──────────────────────────────
  const [showAbout, setShowAbout] = useState(false);
  const [aboutSaving, setAboutSaving] = useState(false);
  const [interestInput, setInterestInput] = useState("");
  const [about, setAbout] = useState({ bio: "", interests: [], country: "", city: "", githubUrl: "", websiteUrl: "", twitterUrl: "", linkedinUrl: "" });

  useEffect(() => {
    api.get("/profile/me").then(({ data: p }) => {
      const parse = (s) => { try { const v = JSON.parse(s || "[]"); return Array.isArray(v) ? v : []; } catch { return []; } };
      setAbout({
        bio: p.bio || "", interests: parse(p.profile?.interests), country: p.profile?.country || "", city: p.profile?.city || "",
        githubUrl: p.profile?.githubUrl || "", websiteUrl: p.profile?.websiteUrl || "", twitterUrl: p.profile?.twitterUrl || "", linkedinUrl: p.profile?.linkedinUrl || "",
      });
    }).catch(() => {});
  }, []);

  const addInterest = () => {
    const t = interestInput.trim().toLowerCase();
    if (t && !about.interests.includes(t) && about.interests.length < 15) { setAbout((a) => ({ ...a, interests: [...a.interests, t] })); setInterestInput(""); }
  };
  const saveAbout = async () => {
    setAboutSaving(true);
    try {
      await api.patch("/profile/me", { bio: about.bio });
      await api.patch("/profile/me/extended", { interests: about.interests, country: about.country, city: about.city, githubUrl: about.githubUrl || null, websiteUrl: about.websiteUrl || null, twitterUrl: about.twitterUrl || null, linkedinUrl: about.linkedinUrl || null });
      toast.success("Über dich gespeichert! ✨");
    } catch { toast.error("Speichern fehlgeschlagen"); }
    finally { setAboutSaving(false); }
  };

  /* ── Queries ── */
  const { data, isLoading } = useQuery({
    queryKey: ["student", "me"],
    queryFn:  () => api.get("/students/me").then((r) => r.data),
  });

  const { data: achData } = useQuery({
    queryKey: ["my-achievements"],
    queryFn:  () => api.get("/achievements/mine").then((r) => r.data),
  });

  const { data: badgesData } = useQuery({
    queryKey: ["gamification-badges-mine"],
    queryFn:  () => api.get("/gamification/badges/mine").then((r) => r.data),
  });

  const { data: titlesData } = useQuery({
    queryKey: ["gamification-titles-mine"],
    queryFn:  () => api.get("/gamification/titles/mine").then((r) => r.data),
  });

  const { data: friendsData } = useQuery({
    queryKey: ["social-friends"],
    queryFn:  () => api.get("/social/friends").then((r) => r.data),
    staleTime: 60_000,
  });

  const { data: lbData } = useQuery({
    queryKey: ["leaderboard-all"],
    queryFn:  () => api.get("/leaderboard").then((r) => r.data),
    staleTime: 60_000,
  });

  /* ── Mutations ── */
  const changeAvatar = useMutation({
    mutationFn: (avatar) => api.patch("/users/me/avatar", { avatar }),
    onSuccess: (_, avatar) => {
      updateAvatar(avatar);
      toast.success(`Avatar geändert!`);
      qc.invalidateQueries({ queryKey: ["student", "me"] });
      setShowAvatarPicker(false);
    },
    onError: () => toast.error("Fehler beim Ändern"),
  });

  const handleCustomUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("avatar", file);
      await api.post("/users/me/avatar-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshMe();
      toast.success("Profilbild gesetzt! 🎉");
      qc.invalidateQueries({ queryKey: ["student", "me"] });
      setShowAvatarPicker(false);
    } catch { toast.error("Upload fehlgeschlagen"); }
    finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeCustomAvatar = async () => {
    try {
      await api.delete("/users/me/avatar-upload");
      await refreshMe();
      toast.success("Profilbild entfernt");
      qc.invalidateQueries({ queryKey: ["student", "me"] });
    } catch { toast.error("Fehler beim Entfernen"); }
  };

  if (isLoading) return (
    <div className="flex justify-center pt-20">
      <div className="relative w-12 h-12">
        <div className="absolute inset-0 rounded-full border-2 border-t-violet-500 border-r-cyan-500 border-b-transparent border-l-transparent animate-spin" />
      </div>
    </div>
  );

  /* ── Derived data ── */
  const profile        = data?.student;
  const xp             = profile?.xp    || 0;
  const coins          = profile?.coins || 0;
  const streak         = profile?.streak || 0;
  const level          = getLevel(xp);
  const levelProgress  = getLevelProgress ? getLevelProgress(xp) : null;
  const shadow         = getShadow(xp);
  const earned         = achData?.achievements || [];
  const myBadges       = badgesData?.badges    || [];
  const myTitles       = titlesData?.titles    || [];
  const friends        = friendsData?.friends  || [];
  const lbEntries      = lbData?.leaderboard   || lbData?.entries || [];
  const showcaseBadges = myBadges.filter((sb) => sb.isShowcase).slice(0, 6);
  const equippedTitle  = myTitles.find((st) => st.isEquipped);
  const quizCount      = profile?.quizAttempts?.length || 0;
  const myRank         = lbEntries.findIndex((e) => e.userId === user?.id || e.id === profile?.id);
  const rank           = myRank >= 0 ? myRank + 1 : null;
  const totalXPInClass = lbEntries.length > 0 ? lbEntries[0]?.xp || 0 : xp;
  const joinDate       = user?.createdAt ? new Date(user.createdAt).toLocaleDateString("de-DE", { month: "short", year: "numeric" }) : null;

  return (
    <div className="space-y-4 animate-fade-in">

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* HERO CARD                                                           */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <div
        className="relative rounded-3xl overflow-hidden border"
        style={{
          borderColor: `${shadow.color}30`,
          background: `radial-gradient(ellipse at 50% -20%, ${shadow.color}18 0%, transparent 65%), rgba(10,10,35,0.95)`,
        }}
      >
        {/* Aurora top accent */}
        <div
          className="absolute inset-x-0 top-0 h-0.5"
          style={{ background: `linear-gradient(90deg, transparent, ${shadow.color}, #06b6d4, transparent)` }}
        />

        {/* Background hex pattern */}
        <div className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: `repeating-linear-gradient(60deg, #fff 0, #fff 1px, transparent 0, transparent 50%)`, backgroundSize: "20px 35px" }} />

        <div className="relative z-10 p-6">
          {/* Top row: join date + StudoX Core hint */}
          <div className="flex items-center justify-between mb-4">
            {joinDate && (
              <span className="text-[10px] text-gray-600 font-bold uppercase tracking-widest">
                🗓️ Mitglied seit {joinDate}{membershipText(user?.createdAt) ? ` · ${membershipText(user.createdAt).dur}` : ""}
              </span>
            )}
            <button
              onClick={() => navigate("/core")}
              className="text-[10px] px-2.5 py-1 rounded-full border font-bold transition-all hover:scale-105 active:scale-95"
              style={{ borderColor: "rgba(139,92,246,0.3)", color: "#8b5cf6", background: "rgba(139,92,246,0.06)" }}
            >
              StudoX Core →
            </button>
          </div>

          {/* Avatar + Name + Title */}
          <div className="flex flex-col items-center mb-5">
            <div className="relative mb-3">
              <XPRing xp={xp} size={110} />
              <button
                onClick={() => setShowAvatarPicker(!showAvatarPicker)}
                className="absolute inset-0 flex items-center justify-center group"
              >
                <div
                  className="w-18 h-18 rounded-2xl bg-nexus-s2 border-2 flex items-center justify-center overflow-hidden transition-transform duration-200 group-hover:scale-105"
                  style={{ width: 72, height: 72, borderColor: `${shadow.color}60`, boxShadow: `0 0 20px ${shadow.color}40` }}
                >
                  {user?.avatarUrl ? (
                    <img src={`${BACKEND_URL}${user.avatarUrl}`} alt="Avatar" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-4xl">{user?.avatar || "🦊"}</span>
                  )}
                </div>
                <div className="absolute inset-0 rounded-full flex items-end justify-end pb-0.5 pr-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <div className="w-6 h-6 rounded-full bg-violet-600 border-2 border-nexus-bg flex items-center justify-center text-xs">✏️</div>
                </div>
              </button>

              {/* Online indicator */}
              <div className="absolute bottom-1 right-1 w-3.5 h-3.5 rounded-full bg-green-500 border-2 border-nexus-bg"
                style={{ boxShadow: "0 0 6px #22c55e" }} />
            </div>

            <h2 className="text-2xl font-black tracking-tight">{user?.name}</h2>

            {equippedTitle && (
              <p
                className="text-sm font-black mt-1 tracking-wide"
                style={equippedTitle.title.gradient
                  ? { background: equippedTitle.title.gradient, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }
                  : { color: equippedTitle.title.color }}
              >
                {equippedTitle.title.name}
              </p>
            )}

            <div className="mt-2 flex items-center gap-2">
              <ShadowBadge xp={xp} size="md" />
              {rank && <RankMedal rank={rank} />}
            </div>
          </div>

          {/* XP Progress */}
          <XPBar xp={xp} size="md" />
        </div>
      </div>

      {/* ── Avatar picker ─────────────────────────────────────────────── */}
      {showAvatarPicker && (
        <Card className="p-4 animate-slide-up">
          <div className="flex items-center justify-between mb-3">
            <p className="font-black text-sm flex items-center gap-2">
              <span>🎭</span><span>Profilbild wählen</span>
            </p>
            <div className="flex gap-1">
              {[{ id: "emoji", label: "Emoji" }, { id: "custom", label: "Foto" }].map((t) => (
                <button key={t.id} onClick={() => setAvatarTab(t.id)}
                  className={clsx(
                    "px-3 py-1 rounded-lg text-xs font-bold border transition-all",
                    avatarTab === t.id
                      ? "bg-violet-600 border-violet-500 text-white"
                      : "bg-nexus-s2 border-nexus-border text-gray-400 hover:border-violet-500/30"
                  )}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {avatarTab === "emoji" ? (
            <div className="grid grid-cols-6 gap-2">
              {AVATARS.map((a) => (
                <button key={a} onClick={() => changeAvatar.mutate(a)}
                  className={clsx(
                    "w-11 h-11 rounded-xl flex items-center justify-center text-2xl border-2 transition-all duration-200",
                    "hover:scale-110 active:scale-95",
                    user?.avatar === a && !user?.avatarUrl
                      ? "border-violet-500 bg-violet-500/20 shadow-flow scale-110"
                      : "border-nexus-border bg-nexus-s2 hover:border-violet-500/50"
                  )}>
                  {a}
                </button>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {user?.avatarUrl && (
                <div className="flex items-center gap-3 p-3 bg-nexus-s2 rounded-xl border border-nexus-border">
                  <img src={`${BACKEND_URL}${user.avatarUrl}`} alt="Aktuell"
                    className="w-14 h-14 rounded-xl object-cover border-2 border-violet-500/50" />
                  <div className="flex-1">
                    <p className="text-xs font-bold text-white">Aktuelles Foto</p>
                    <p className="text-[10px] text-gray-500 mt-0.5">Benutzerdefiniertes Profilbild</p>
                  </div>
                  <button onClick={removeCustomAvatar}
                    className="px-3 py-1.5 rounded-lg bg-red-900/30 border border-red-800/40 text-red-400 text-xs font-bold hover:bg-red-900/50 transition-colors">
                    Entfernen
                  </button>
                </div>
              )}
              <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif"
                className="hidden" onChange={handleCustomUpload} />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="w-full py-4 rounded-2xl border-2 border-dashed transition-all duration-200 flex flex-col items-center gap-2"
                style={{ borderColor: uploading ? "rgba(139,92,246,0.5)" : "rgba(139,92,246,0.3)", background: "rgba(139,92,246,0.04)" }}
              >
                {uploading ? (
                  <><div className="w-6 h-6 border-2 border-t-violet-500 border-r-violet-500 border-b-transparent border-l-transparent rounded-full animate-spin" />
                    <span className="text-xs font-bold text-violet-400">Lädt hoch…</span></>
                ) : (
                  <><svg width="20" height="20" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="#8b5cf6" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/>
                    </svg>
                    <span className="text-xs font-bold text-violet-400">Foto hochladen</span>
                    <span className="text-[10px] text-gray-600">JPG, PNG, WebP · max 5 MB</span></>
                )}
              </button>
            </div>
          )}
        </Card>
      )}

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* ÜBER DICH — bio, interests, links (universal /api/profile/*)        */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <Card className="p-4">
        <button onClick={() => setShowAbout((v) => !v)} className="flex w-full items-center justify-between">
          <p className="font-black text-sm flex items-center gap-2"><span>📝</span><span>Über dich</span></p>
          <span className="text-xs text-gray-500">{showAbout ? "Schließen" : (about.bio || about.interests.length ? "Bearbeiten" : "Hinzufügen")}</span>
        </button>
        {!showAbout && about.bio && <p className="mt-3 text-sm text-gray-400 leading-relaxed">{about.bio}</p>}
        {!showAbout && about.interests.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {about.interests.map((t) => <span key={t} className="text-xs px-2.5 py-1 rounded-full bg-violet-500/10 text-violet-300 border border-violet-500/25 font-medium">{t}</span>)}
          </div>
        )}
        {showAbout && (
          <div className="mt-4 space-y-3">
            <textarea value={about.bio} onChange={(e) => setAbout((a) => ({ ...a, bio: e.target.value }))} maxLength={300} rows={3}
              placeholder="Erzähl anderen etwas über dich…" className="w-full bg-nexus-s2 border border-nexus-border rounded-xl p-3 text-sm text-white placeholder-gray-500 resize-none focus:outline-none focus:border-violet-500" />
            <div className="grid grid-cols-2 gap-2">
              <input value={about.country} onChange={(e) => setAbout((a) => ({ ...a, country: e.target.value }))} placeholder="Land"
                className="bg-nexus-s2 border border-nexus-border rounded-xl px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-violet-500" />
              <input value={about.city} onChange={(e) => setAbout((a) => ({ ...a, city: e.target.value }))} placeholder="Stadt"
                className="bg-nexus-s2 border border-nexus-border rounded-xl px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-violet-500" />
            </div>
            <div className="flex gap-2">
              <input value={interestInput} onChange={(e) => setInterestInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addInterest())}
                placeholder="Interesse hinzufügen…" className="flex-1 bg-nexus-s2 border border-nexus-border rounded-xl px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-violet-500" />
              <button onClick={addInterest} className="px-3.5 rounded-xl bg-violet-600/25 border border-violet-500/40 text-violet-300 font-bold">+</button>
            </div>
            {about.interests.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {about.interests.map((t) => (
                  <span key={t} className="flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-violet-500/10 text-violet-300 border border-violet-500/25 font-medium">
                    {t}<button onClick={() => setAbout((a) => ({ ...a, interests: a.interests.filter((x) => x !== t) }))}>✕</button>
                  </span>
                ))}
              </div>
            )}
            <Button full loading={aboutSaving} onClick={saveAbout}>Speichern</Button>
          </div>
        )}
      </Card>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* STATS GRID                                                          */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <div className="grid grid-cols-3 gap-2">
        <StatTile icon="⭐" label="Level"     value={`Lv.${level}`}                   color="#8b5cf6" />
        <StatTile icon="⚡" label="Gesamt XP" value={xp.toLocaleString("de-DE")}       color="#a78bfa" />
        <StatTile icon="🔥" label="Streak"    value={`${streak}d`}                     color="#f97316" />
        <StatTile icon="🪙" label="Coins"     value={coins.toLocaleString("de-DE")}    color="#fbbf24" />
        <StatTile icon="🎮" label="Quizze"    value={quizCount}                        color="#06b6d4" />
        <StatTile icon="🏷️" label="Badges"    value={myBadges.length}                  color="#22c55e" />
      </div>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* DEINE REISE — Level-System 2.0                                      */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <JourneyPanel xp={xp} user={user} />

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* SOCIAL + LEADERBOARD ROW                                           */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <div className="grid grid-cols-2 gap-3">
        {/* Friends card */}
        <button
          onClick={() => navigate("/friends")}
          className="relative p-4 rounded-2xl border border-nexus-border overflow-hidden text-left group transition-all duration-200 hover:scale-[1.02] hover:border-cyan-500/30 active:scale-[0.98]"
          style={{ background: "rgba(6,182,212,0.05)" }}
        >
          <div className="absolute inset-x-0 top-0 h-px" style={{ background: "linear-gradient(90deg, transparent, #06b6d440, transparent)" }} />
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xl" style={{ filter: "drop-shadow(0 0 8px #06b6d480)" }}>👥</span>
            <span className="text-[10px] font-black uppercase tracking-widest text-cyan-500">Freunde</span>
          </div>
          <p className="text-2xl font-black text-cyan-400 tabular-nums">{friends.length}</p>
          <p className="text-[10px] text-gray-600 mt-0.5">
            {friends.length === 0 ? "Noch keine Freunde" :
             friends.length === 1 ? "aktiver Freund" : "aktive Freunde"}
          </p>
          <div className="mt-2 flex -space-x-1.5">
            {friends.slice(0, 5).map((f, i) => (
              <div key={f.id || i}
                className="w-6 h-6 rounded-full bg-nexus-s2 border border-nexus-border flex items-center justify-center text-xs"
                style={{ zIndex: 5 - i }}>
                {f.friend?.avatar || f.avatar || "🦊"}
              </div>
            ))}
            {friends.length > 5 && (
              <div className="w-6 h-6 rounded-full bg-cyan-500/20 border border-cyan-500/30 flex items-center justify-center text-[9px] font-black text-cyan-400">
                +{friends.length - 5}
              </div>
            )}
          </div>
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-700 group-hover:text-cyan-500 transition-colors text-xs">›</div>
        </button>

        {/* Leaderboard rank card */}
        <button
          onClick={() => navigate("/leaderboard")}
          className="relative p-4 rounded-2xl border border-nexus-border overflow-hidden text-left group transition-all duration-200 hover:scale-[1.02] hover:border-yellow-500/30 active:scale-[0.98]"
          style={{ background: "rgba(251,191,36,0.05)" }}
        >
          <div className="absolute inset-x-0 top-0 h-px" style={{ background: "linear-gradient(90deg, transparent, #fbbf2440, transparent)" }} />
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xl" style={{ filter: "drop-shadow(0 0 8px #fbbf2480)" }}>🏆</span>
            <span className="text-[10px] font-black uppercase tracking-widest text-yellow-500">Rangliste</span>
          </div>
          {rank ? (
            <>
              <p className="text-2xl font-black text-yellow-400 tabular-nums">#{rank}</p>
              <p className="text-[10px] text-gray-600 mt-0.5">
                von {lbEntries.length} Spielern
              </p>
              {rank <= 3 && (
                <p className="text-[10px] mt-1 font-black" style={{ color: rank === 1 ? "#fbbf24" : rank === 2 ? "#9ca3af" : "#f97316" }}>
                  {rank === 1 ? "🥇 Du bist #1!" : rank === 2 ? "🥈 Platz 2" : "🥉 Platz 3"}
                </p>
              )}
            </>
          ) : (
            <>
              <p className="text-2xl font-black text-yellow-400/40">—</p>
              <p className="text-[10px] text-gray-600 mt-0.5">Nicht eingetragen</p>
              <p className="text-[10px] text-yellow-600 mt-1">400 XP zum Beitreten</p>
            </>
          )}
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-700 group-hover:text-yellow-500 transition-colors text-xs">›</div>
        </button>
      </div>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* SHADOW RANK PROGRESS                                               */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <div
        className="p-4 rounded-2xl border relative overflow-hidden"
        style={{
          borderColor: `${shadow.color}35`,
          background: `radial-gradient(ellipse at 0% 50%, ${shadow.color}08 0%, transparent 60%), rgba(13,13,43,0.8)`,
        }}
      >
        <div className="absolute inset-y-0 left-0 w-0.5" style={{ background: shadow.color, boxShadow: `0 0 8px ${shadow.color}` }} />
        <div className="flex items-center gap-3 pl-2">
          <span className="text-4xl animate-float flex-shrink-0" style={{ filter: `drop-shadow(0 0 14px ${shadow.color})` }}>
            {shadow.emoji}
          </span>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <p className="font-black text-sm" style={{ color: shadow.color }}>{shadow.name}</p>
            </div>
            <p className="text-xs text-gray-400 mt-0.5">{shadow.description}</p>
          </div>
        </div>

        {shadow.nextThreshold && (
          <div className="mt-3 pl-2">
            <div className="flex justify-between text-[10px] font-bold mb-1.5">
              <span className="text-gray-500">Nächster Rang</span>
              <span style={{ color: shadow.color }}>{(shadow.nextThreshold - xp).toLocaleString("de-DE")} XP fehlen</span>
            </div>
            <div className="h-2 rounded-full overflow-hidden" style={{ background: `${shadow.color}15` }}>
              <div
                className="h-full rounded-full transition-all duration-1000"
                style={{
                  width: `${Math.min(((xp - shadow.threshold) / (shadow.nextThreshold - shadow.threshold)) * 100, 100)}%`,
                  background: `linear-gradient(90deg, ${shadow.color}80, ${shadow.color})`,
                  boxShadow: `0 0 8px ${shadow.color}`,
                }}
              />
            </div>
          </div>
        )}
      </div>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* GAMIFICATION QUICK-NAV                                             */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <div className="grid grid-cols-3 gap-2">
        {[
          { path: "/badges",  emoji: "🏷️", label: "Badges",  count: myBadges.length,  color: "#8b5cf6" },
          { path: "/titles",  emoji: "👑", label: "Titel",   count: myTitles.length,  color: "#fbbf24" },
          { path: "/events",  emoji: "🎪", label: "Events",  count: null,             color: "#06b6d4" },
        ].map((item) => (
          <button
            key={item.path}
            onClick={() => navigate(item.path)}
            className="flex flex-col items-center gap-1.5 p-3.5 rounded-2xl border border-nexus-border hover:border-current/30 transition-all duration-200 hover:scale-[1.03] active:scale-[0.97] group relative overflow-hidden"
            style={{ background: `${item.color}08` }}
          >
            <div className="absolute inset-x-0 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${item.color}50, transparent)` }} />
            <span className="text-2xl" style={{ filter: `drop-shadow(0 0 8px ${item.color}70)` }}>{item.emoji}</span>
            <span className="text-xs font-black" style={{ color: item.color }}>{item.label}</span>
            {item.count !== null && (
              <span className="text-[9px] text-gray-500">{item.count} erhalten</span>
            )}
          </button>
        ))}
      </div>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* BADGE SHOWCASE                                                      */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {showcaseBadges.length > 0 && (
        <Card className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-black text-sm flex items-center gap-2">
              <span>📌</span><span>Badge Showcase</span>
            </h3>
            <button onClick={() => navigate("/badges")} className="text-[10px] text-gray-500 hover:text-violet-400 transition-colors">
              Alle →
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {showcaseBadges.map((sb) => {
              const color = sb.badge.color || "#8b5cf6";
              return (
                <div
                  key={sb.id}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl border transition-all hover:scale-[1.03]"
                  style={{ borderColor: `${color}40`, background: `${color}10` }}
                  title={sb.badge.description}
                >
                  <span className="text-lg" style={{ filter: `drop-shadow(0 0 6px ${color}70)` }}>{sb.badge.icon}</span>
                  <span className="text-xs font-bold" style={{ color }}>{sb.badge.name}</span>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* RECENT ACHIEVEMENTS                                                 */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {earned.length > 0 && (
        <Card className="p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-black text-sm flex items-center gap-2">
              <span className="text-yellow-400">🏅</span><span>Neueste Achievements</span>
            </h3>
            <button onClick={() => navigate("/achievements")} className="text-[10px] text-gray-500 hover:text-yellow-400 transition-colors">
              Alle →
            </button>
          </div>
          <div className="space-y-2">
            {earned.slice(0, 4).map((ea) => {
              const a    = ea.achievement;
              const meta = RARITY_META[a.rarity] || RARITY_META.common;
              return (
                <div key={ea.id} className="flex items-center gap-3 p-2.5 rounded-xl border border-nexus-border bg-nexus-s2/50 hover:bg-nexus-s2 transition-colors">
                  <span className="text-2xl flex-shrink-0" style={{ filter: `drop-shadow(0 0 6px ${meta.color}60)` }}>{a.icon}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-xs truncate">{a.title}</p>
                    <p className="text-[10px] text-gray-600">{new Date(ea.awardedAt).toLocaleDateString("de-DE")}</p>
                  </div>
                  <span
                    className="text-[9px] font-black px-2 py-0.5 rounded-full border flex-shrink-0"
                    style={{ color: meta.color, borderColor: `${meta.color}40`, background: `${meta.color}10` }}
                  >
                    {meta.label}
                  </span>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* ACTIVITY BREAKDOWN                                                  */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <Card className="p-4">
        <h3 className="font-black text-sm mb-4 flex items-center gap-2">
          <span className="text-violet-400">📈</span>
          <span>Aktivität</span>
        </h3>
        <div className="space-y-3">
          <ActivityBar value={xp}        max={Math.max(totalXPInClass, xp)} color="#8b5cf6" label="Gesamt XP" />
          <ActivityBar value={streak}    max={Math.max(streak, 30)}         color="#f97316" label="Streak (Tage)" />
          <ActivityBar value={quizCount} max={Math.max(quizCount, 50)}      color="#06b6d4" label="Gelöste Quizze" />
          <ActivityBar value={myBadges.length} max={Math.max(myBadges.length, 20)} color="#22c55e" label="Errungene Badges" />
        </div>
      </Card>

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* LOGOUT                                                              */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <button
        onClick={() => { logout(); toast("Bis zum nächsten Mal! 👋"); navigate("/login"); }}
        className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl border border-red-900/40 bg-red-950/20 hover:bg-red-950/40 hover:border-red-800/60 transition-all text-sm font-bold text-red-400 hover:text-red-300"
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" strokeWidth="2" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
        </svg>
        Abmelden
      </button>

      <div className="pb-2" />
    </div>
  );
}
